import { FC, useCallback, useMemo, useState } from "react";
import { Typography } from "@mui/material";
import {
  DataGridPro as DataGrid,
  GridColDef,
  GridRowParams,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColumnHeaderTitle,
} from "@mui/x-data-grid-pro";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import countNonNullEntries from "src/utils/data/transform/countNonNullEntries";
import CustomDetailPanelToggle from "src/Pages/Company/Absences/Tables/CustomDetailPanelToggle";
import {
  HistoryAction,
  IAbsenceRequestHistoryEntry,
  RequesterType,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IAbsenceTypeHistory } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import AbsenceTypeHistoryDetails from "./AbsenceTypeHistoryDetails";

interface IAbsenceTypeHistoryProps {
  data: IAbsenceTypeHistory;
}

const AbsenceTypeHistory: FC<IAbsenceTypeHistoryProps> = ({ data }) => {
  const logStatus = useLogStatus();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  if (!data) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No Absence Type history data provided.",
      file: "AbsenceTypeHistory.tsx",
    });
  }

  const columns = useMemo<GridColDef<IAbsenceRequestHistoryEntry>[]>(
    () => [
      {
        field: "requester",
        headerName: __("Autor zmian"),
        type: "string",
        width: 300,
        valueGetter: ({ type, firstName, lastName }) => {
          if (type === RequesterType.SYSTEM) {
            return __("System");
          }
          if (type === RequesterType.OMB) {
            return __("Operator MyBenefit");
          }
          if (type === RequesterType.UNKNOWN) {
            return __("Brak danych");
          }
          return `${firstName} ${lastName}`;
        },
      },
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 200,
        renderHeader: () => (
          <GridColumnHeaderTitle label={__("Szczegóły")} columnWidth={200} />
        ),
        renderCell: (params) => {
          if (params.row.action === HistoryAction.CREATION) {
            return __("Utworzenie szablonu");
          }

          const detailRowCount = params.row.details
            ? countNonNullEntries(
                params.row.details as unknown as Record<string, unknown>
              )
            : 0;

          return detailRowCount ? <CustomDetailPanelToggle /> : "-";
        },
      },
      {
        field: "actionTime",
        headerName: __("Data zmiany"),
        type: "dateTime",
        sortable: true,
        width: 175,
        valueGetter: (value) => new Date(value),
      },
    ],
    []
  );

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams) => (
      <AbsenceTypeHistoryDetails data={row.details} />
    ),
    []
  );

  const getDetailPanelHeight = useCallback(({ row }: GridRowParams) => {
    const detailRowCount = countNonNullEntries(row.details);

    return 120 + detailRowCount * 60;
  }, []);

  return (
    <>
      <Typography variant="h5">{__("Historia zmian szablonu")}</Typography>
      <DataGrid
        rows={data.entries}
        columns={columns}
        getRowId={(row) => `${row.order}-${row.actionTime}`}
        initialState={{
          sorting: {
            sortModel: [{ field: "actionTime", sort: "desc" }],
          },
        }}
        disableColumnMenu
        disableEval
        hideFooterRowCount
        density="comfortable"
        rowSelection={false}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25, 50, 100]}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
      />
    </>
  );
};

export default AbsenceTypeHistory;
